import authService from "./AuthService";
import {
  AddUser_AddUserRequest,
  ApiError,
  ControlBlocksService,
  DeleteLinkUserAndDevice_LinkUserAndDeviceCommand,
  GetUsers_UserDto,
  LinkUserAndDevice_DeleteLinkUserAndDeviceCommand,
  UsersService,
} from "../api";

const isApiError = (error: unknown): error is ApiError => {
  return error instanceof ApiError;
};

const buildApiErrorMessage = (error: unknown, fallbackMessage: string) => {
  if (isApiError(error) && error.status === 400) {
    const body = error.body as {
      message?: string;
      errors?: Record<string, string[] | string>;
    };

    if (body?.errors) {
      const messages = Object.values(body.errors).flatMap((value) =>
        Array.isArray(value) ? value : [value]
      );

      if (messages.length > 0) {
        return messages.join(", ");
      }
    }

    if (body?.message) {
      return body.message;
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallbackMessage;
};

const getAuthHeaders = async () => {
  const token = await authService.getAccessToken();

  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token ?? ""}`,
  };
};

export const UsersApi = {
  getUsers: async (): Promise<GetUsers_UserDto[]> => {
    const response = await UsersService.getApiUsers();
    return response.users ?? [];
  },

  createUser: async (email: string, phone: string): Promise<boolean> => {
    const payload: AddUser_AddUserRequest = {
      email: email || "",
      phone: phone || "",
    };

    try {
      await UsersService.postApiUsers(payload);
      return true;
    } catch (error) {
      throw new Error(buildApiErrorMessage(error, "Ошибка при создании пользователя"));
    }
  },

  getUser: async (userId: string): Promise<GetUsers_UserDto | null> => {
    const response = await UsersService.getApiUsers1(userId);
    return response.user ?? null;
  },

  deleteUser: async (userId: string): Promise<boolean> => {
    await UsersService.deleteApiUsers(userId);
    return true;
  },

  setUserBlocked: async (userId: string, isBlocked: boolean): Promise<boolean> => {
    const response = await fetch(`/api/users/${encodeURIComponent(userId)}/block`, {
      method: "PATCH",
      headers: await getAuthHeaders(),
      body: JSON.stringify({ isBlocked }),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      const message = buildApiErrorMessage(
        { message: errorBody?.message } as Error,
        "Ошибка при изменении статуса пользователя"
      );
      throw new Error(message);
    }

    return true;
  },

  linkDevice: async (userId: string, deviceId: string): Promise<boolean> => {
    const payload: DeleteLinkUserAndDevice_LinkUserAndDeviceCommand = {
      userId,
      deviceId,
    };

    await ControlBlocksService.postApiControlBlocksLink(payload);
    return true;
  },

  unlinkDevice: async (userId: string, deviceId: string): Promise<boolean> => {
    const payload: LinkUserAndDevice_DeleteLinkUserAndDeviceCommand = {
      userId,
      deviceId,
    };

    await ControlBlocksService.deleteApiControlBlocksLink(payload);
    return true;
  },
};

export default UsersApi;
